// BucketFilterLinker — Draws animated Bezier curves from a source point to matching tiles
//
// When a bucket filter is active, this component reads tile positions from the
// instanced mesh attributes (aLayout/aOldLayout interpolated via uLayoutMix),
// sorts them by distance to camera, picks the closest N, and renders cubic
// Bezier curves using the LinesAA system with WAVE field animation.

import React, { useRef, useMemo, useCallback } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { LinesAAMesh } from '@arkturian/wave-engine/lines';
import { setLines } from '@arkturian/wave-engine/lines';
import type { LineSegment } from '@arkturian/wave-engine/lines';
import { activeBucketPositionRef } from './BucketButtons3D';

interface BucketFilterLinkerProps {
    meshRef: React.RefObject<THREE.InstancedMesh>;
    uniformsRef: React.MutableRefObject<any>;
    matchingIndices: number[] | null;
    enabled: boolean;
    maxLines?: number;
    color?: THREE.Color;
    lineFace?: 'front' | 'back' | 'center'; // which face the lines connect to
}

/** Segments per Bezier curve (higher = smoother) */
const BEZIER_SUBDIVISIONS = 12;

// Temp vectors — allocated once, reused every frame
const _pos = new THREE.Vector3();
const _posOld = new THREE.Vector3();
const _faceNormal = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _tilePos = new THREE.Vector3();
const _mid = new THREE.Vector3();
const _cp1 = new THREE.Vector3();
const _cp2 = new THREE.Vector3();
const _camToTile = new THREE.Vector3();
const _perpendicular = new THREE.Vector3();

/** Read interpolated position for particle i from aLayout / aOldLayout */
function getParticlePos(
    layoutAttr: THREE.InstancedBufferAttribute,
    oldLayoutAttr: THREE.InstancedBufferAttribute,
    i: number,
    mix: number,
    out: THREE.Vector3,
): void {
    _pos.set(layoutAttr.getX(i), layoutAttr.getY(i), layoutAttr.getZ(i));
    _posOld.set(oldLayoutAttr.getX(i), oldLayoutAttr.getY(i), oldLayoutAttr.getZ(i));
    out.lerpVectors(_posOld, _pos, mix);
}

/** Evaluate cubic Bezier at parameter t */
function cubicBezier(
    p0: THREE.Vector3,
    p1: THREE.Vector3,
    p2: THREE.Vector3,
    p3: THREE.Vector3,
    t: number,
    out: THREE.Vector3,
): void {
    const it = 1 - t;
    const it2 = it * it;
    const t2 = t * t;
    out.set(
        it2 * it * p0.x + 3 * it2 * t * p1.x + 3 * it * t2 * p2.x + t2 * t * p3.x,
        it2 * it * p0.y + 3 * it2 * t * p1.y + 3 * it * t2 * p2.y + t2 * t * p3.y,
        it2 * it * p0.z + 3 * it2 * t * p1.z + 3 * it * t2 * p2.z + t2 * t * p3.z,
    );
}

const BucketFilterLinker: React.FC<BucketFilterLinkerProps> = ({
    meshRef,
    uniformsRef,
    matchingIndices,
    enabled,
    maxLines = 60,
    color,
    lineFace = 'front',
}) => {
    const geoRef = useRef<THREE.InstancedBufferGeometry | null>(null);
    const linesRef = useRef<LineSegment[]>([]);
    const readyRef = useRef(false);
    const prevIndicesRef = useRef<number[] | null>(null);
    const sortedIndicesRef = useRef<number[]>([]);
    const sourceRef = useRef(new THREE.Vector3());
    const opacityRef = useRef(0);

    const { camera } = useThree();

    const lineColor = useMemo(() => color ?? new THREE.Color(0.3, 0.6, 1.0), [color]);

    const onGeometryReady = useCallback((geo: THREE.InstancedBufferGeometry) => {
        geoRef.current = geo;
        readyRef.current = true;
    }, []);

    // Dummy line so LinesAAMesh creates geometry on mount
    const initialLines = useMemo<LineSegment[]>(() => [{
        start: new THREE.Vector3(0, 0, 0),
        end: new THREE.Vector3(0, 0.001, 0),
        thickness: 0,
    }], []);

    // Pre-allocate line segments pool (maxLines * BEZIER_SUBDIVISIONS sub-segments)
    const maxSegments = maxLines * BEZIER_SUBDIVISIONS;
    const segmentPool = useMemo(() => {
        const pool: LineSegment[] = [];
        for (let i = 0; i < maxSegments; i++) {
            pool.push({
                start: new THREE.Vector3(),
                end: new THREE.Vector3(),
                startColor: new THREE.Color(),
                endColor: new THREE.Color(),
                startAlpha: 1.0,
                endAlpha: 1.0,
                thickness: 1.0,
                segments: 1, // already subdivided manually
            });
        }
        return pool;
    }, [maxSegments]);

    // Temp vectors for Bezier evaluation — per-curve, reused each frame
    const _bStart = useMemo(() => new THREE.Vector3(), []);
    const _bEnd = useMemo(() => new THREE.Vector3(), []);

    useFrame((_state, delta) => {
        if (!readyRef.current) return;
        const geo = geoRef.current;
        if (!geo) return;

        // Animate opacity: fade in when enabled + has matches, fade out otherwise
        const targetOpacity = (enabled && matchingIndices && matchingIndices.length > 0) ? 0.7 : 0;
        const fadeSpeed = 4.0;
        opacityRef.current += (targetOpacity - opacityRef.current) * Math.min(1, delta * fadeSpeed);

        // If fully faded out, clear geometry and return
        if (opacityRef.current < 0.01) {
            geo.instanceCount = 0;
            return;
        }

        const mesh = meshRef.current;
        const uniforms = uniformsRef.current;
        if (!mesh || !uniforms) return;

        const layoutAttr = mesh.geometry.getAttribute('aLayout') as THREE.InstancedBufferAttribute;
        const oldLayoutAttr = mesh.geometry.getAttribute('aOldLayout') as THREE.InstancedBufferAttribute;
        if (!layoutAttr || !oldLayoutAttr) return;

        const mix = uniforms.uLayoutMix?.value ?? 1.0;

        // Rebuild sorted indices when matchingIndices reference changes
        if (matchingIndices !== prevIndicesRef.current) {
            prevIndicesRef.current = matchingIndices;

            if (!matchingIndices || matchingIndices.length === 0) {
                sortedIndicesRef.current = [];
            } else {
                // Sort by distance to camera, take closest maxLines
                const camPos = camera.position;
                const withDist: { idx: number; dist: number }[] = [];

                for (let i = 0; i < matchingIndices.length; i++) {
                    const idx = matchingIndices[i];
                    getParticlePos(layoutAttr, oldLayoutAttr, idx, mix, _tilePos);
                    const dist = _tilePos.distanceToSquared(camPos);
                    withDist.push({ idx, dist });
                }

                withDist.sort((a, b) => a.dist - b.dist);
                sortedIndicesRef.current = withDist.slice(0, maxLines).map(d => d.idx);
            }
        }

        const indices = sortedIndicesRef.current;
        if (indices.length === 0) {
            geo.instanceCount = 0;
            return;
        }

        // Source point: active bucket button position (3D), fallback to camera offset
        if (activeBucketPositionRef.current) {
            sourceRef.current.copy(activeBucketPositionRef.current);
        } else {
            sourceRef.current.copy(camera.position);
            sourceRef.current.y -= 6;
        }

        const source = sourceRef.current;
        const opacity = opacityRef.current;
        let segIdx = 0;

        const quatAttr = mesh.geometry.getAttribute('aQuaternion') as THREE.InstancedBufferAttribute;
        const targetAttr = mesh.geometry.getAttribute('aTarget') as THREE.InstancedBufferAttribute;

        for (let i = 0; i < indices.length && segIdx + BEZIER_SUBDIVISIONS <= maxSegments; i++) {
            const particleIdx = indices[i];
            getParticlePos(layoutAttr, oldLayoutAttr, particleIdx, mix, _tilePos);

            // Offset to front or back face center
            if (lineFace !== 'center' && quatAttr) {
                _quat.set(quatAttr.getX(particleIdx), quatAttr.getY(particleIdx), quatAttr.getZ(particleIdx), quatAttr.getW(particleIdx));
                _faceNormal.set(0, 0, lineFace === 'front' ? 1 : -1).applyQuaternion(_quat);
                const depth = targetAttr ? targetAttr.getY(particleIdx) * layoutAttr.getW(particleIdx) * 0.5 : 0.01;
                _tilePos.addScaledVector(_faceNormal, depth);
            }

            // Compute control points for a nice arc
            _mid.lerpVectors(source, _tilePos, 0.5);

            // Direction from camera to tile (used for perpendicular offset)
            _camToTile.subVectors(_tilePos, source);
            const spanLen = _camToTile.length();

            // Perpendicular vector: cross with world up, normalize
            _perpendicular.crossVectors(_camToTile, camera.up).normalize();

            // Arc offset: varies per line for visual spread
            const arcSign = (i % 2 === 0) ? 1 : -1;
            const arcSpread = 0.15 + (i / Math.max(1, indices.length)) * 0.25;
            const arcOffset = spanLen * arcSpread * arcSign;

            // CP1: 1/3 along the line, offset perpendicular + upward
            _cp1.lerpVectors(source, _tilePos, 0.33);
            _cp1.x += _perpendicular.x * arcOffset;
            _cp1.y += spanLen * 0.2;
            _cp1.z += _perpendicular.z * arcOffset;

            // CP2: 2/3 along the line, offset perpendicular (less) + upward (less)
            _cp2.lerpVectors(source, _tilePos, 0.66);
            _cp2.x += _perpendicular.x * arcOffset * 0.5;
            _cp2.y += spanLen * 0.1;
            _cp2.z += _perpendicular.z * arcOffset * 0.5;

            // Subdivide the Bezier curve into segments
            for (let s = 0; s < BEZIER_SUBDIVISIONS; s++) {
                const t0 = s / BEZIER_SUBDIVISIONS;
                const t1 = (s + 1) / BEZIER_SUBDIVISIONS;

                cubicBezier(source, _cp1, _cp2, _tilePos, t0, _bStart);
                cubicBezier(source, _cp1, _cp2, _tilePos, t1, _bEnd);

                const seg = segmentPool[segIdx];
                seg.start.copy(_bStart);
                seg.end.copy(_bEnd);

                // Color gradient: faded at source, bright at tile
                const alpha0 = opacity * (0.1 + 0.9 * t0);
                const alpha1 = opacity * (0.1 + 0.9 * t1);

                seg.startColor!.copy(lineColor);
                seg.endColor!.copy(lineColor);
                seg.startAlpha = alpha0;
                seg.endAlpha = alpha1;
                seg.thickness = 0.8 + t0 * 0.4; // thickens toward tile

                segIdx++;
            }
        }

        // Only pass the active portion of the pool to setLines
        linesRef.current = segmentPool.slice(0, segIdx);
        setLines(geo, linesRef.current);
    });

    if (!enabled && opacityRef.current < 0.01) return null;

    return (
        <LinesAAMesh
            lines={initialLines}
            baseRadius={0.004}
            opacity={1.0}
            fieldMode={1}
            fieldStrength={0.4}
            waveFreq={4.0}
            waveAmplitude={0.15}
            waveSpeed={3.0}
            waveLayers={2.0}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
            onGeometryReady={onGeometryReady}
        />
    );
};

export default BucketFilterLinker;
